import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, BarChart3, Bot, Building2, CalendarClock, ClipboardCheck, Columns3, FileSearch, IdCard, MessagesSquare, Scale, ShieldCheck, Table2, UserRound, Video, Workflow } from 'lucide-react';
import { DemoRequestDialog } from '@/components/landing/DemoRequestDialog';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { StructuredData } from '@/components/seo/StructuredData';
import { AssistantDemo } from '@/components/mcp/AssistantDemo';
import { CopyField, PromptCard } from '@/components/mcp/CopyField';
import { CheckList, CtaPanel, DataTable, FaqSection, HeroActions, IconCardGrid, InShort, PageShell, RelatedLinks, Reveal, Section, SectionHeading, StepFlow } from '@/components/page/PageKit';
import { PRODUCT_LINKS } from '@/components/page/relatedLinks';
import { Eyebrow, EASE } from '@/components/home/primitives';
import { SKIP_ENTRANCE } from '@/components/home/motionPrefs';
import { MCP_CLIENTS, MCP_DEFINITION, MCP_DOCS_URL, MCP_FAQS, MCP_META, MCP_PROMPTS, MCP_SAFETY, MCP_SERVER_URL, MCP_SETUP_STEPS, MCP_TOOLS, MCP_URL } from '@/data/mcpContent';
import { APP_URL, SITE_URL } from '@/data/marketingContent';
import { faqSchema } from '@/seo/schemas';
import { PUBLISHER } from '@/seo/organization';
import { useSEO } from '@/hooks/useSEO';

const alreadyDone = [
  { icon: FileSearch, title: 'Already screened', body: 'Every application has been scored against your role criteria, with the resume evidence and concerns attached.' },
  { icon: Video, title: 'Already interviewed', body: 'Candidates who passed your rule have answered structured video questions, assessed one answer at a time.' },
  { icon: Scale, title: 'Already ranked', body: 'Completed interviews are scored and ranked, so “who are the strongest?” has a real answer.' },
  { icon: UserRound, title: 'Yours to decide', body: 'The assistant brings back the evidence and a link to the record. Moving someone forward is your call.' },
];

const week = [
  { icon: MessagesSquare, title: 'Monday · you ask', body: '“Open a Senior Account Manager role in London and publish it.” The job is live on your career page, with a link to post anywhere.' },
  { icon: Workflow, title: 'All week · Beatview works', body: 'Every application is screened as it arrives. Your stage rule invites the ones above your threshold to the AI interview — no chat needed.' },
  { icon: CalendarClock, title: 'Every morning · a quick check', body: '“Who’s new since yesterday?” Ask it yourself, or schedule the prompt if your assistant supports recurring tasks.' },
  { icon: Table2, title: 'Thursday · you compare', body: '“Compare the top three.” A comparison table, strongest first, with the answers behind each score one click away.' },
  { icon: ClipboardCheck, title: 'Friday · you decide', body: '“Move Aisha to face-to-face.” The assistant asks you to confirm, Beatview does it, and the action is logged.' },
];

const presentation = [
  { icon: Columns3, title: 'Pipeline → kanban', body: 'One column per stage with counts in the headers, compact candidate cards and a line on where the bottleneck is.' },
  { icon: BarChart3, title: 'Numbers → charts', body: 'Stage distribution, score spread and interviews completed shown as a chart, not a wall of figures.' },
  { icon: IdCard, title: 'One candidate → profile card', body: 'Scores and location, strengths and concerns side by side, the interview breakdown, then your notes.' },
  { icon: Table2, title: 'Several → comparison table', body: 'Strongest first, with the differences that decide it highlighted — and a Beatview link on every row.' },
];

const audiences = [
  { icon: MessagesSquare, title: 'Recruiters on the desk', body: 'Triage new applicants, brief yourself before a call and draft client updates without switching tabs.' },
  { icon: Building2, title: 'Agency owners and team leads', body: 'Ask across every open role: what is live, where it is stuck and who needs action today.' },
  { icon: Bot, title: 'Teams building their own agents', body: 'An approved requisition opens the job and posts the link in Slack; a morning agent pulls the top-scoring new candidates for every role.', href: '/blog/ai-recruiting-dashboard', link: 'Build guide: agent + live dashboard' },
];

const configJson = `{
  "mcpServers": {
    "beatview": {
      "url": "${MCP_SERVER_URL}"
    }
  }
}`;

function Hero({ onDemo }: { onDemo: () => void }) {
  const reduced = useReducedMotion();
  const enter = (delay: number) => ({ initial: reduced || SKIP_ENTRANCE ? false as const : { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay, ease: EASE } });
  return (
    <section className="relative overflow-hidden bg-bv-base pb-16 pt-32 sm:pt-36 lg:pb-24 lg:pt-40" aria-labelledby="mcp-h1">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="bv-hero-grid absolute inset-0" />
        <div className="absolute -top-56 left-[4%] h-[560px] w-[560px] animate-aurora rounded-full bg-[hsl(var(--blue)/0.16)] blur-[110px]" />
        <div className="absolute -top-32 right-[2%] h-[520px] w-[620px] animate-aurora rounded-full bg-[hsl(var(--hero-highlight-start)/0.17)] blur-[120px] [animation-delay:-7s]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-bv-base" />
      </div>
      <div className="relative mx-auto max-w-[1240px] px-5 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div {...enter(0)}><Eyebrow live>Beatview MCP server for recruiting</Eyebrow></motion.div>
          <motion.h1 id="mcp-h1" initial={reduced || SKIP_ENTRANCE ? false : { y: 20 }} animate={{ y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="mt-7 font-display text-[42px] font-semibold leading-[1] tracking-[-0.04em] text-bv-ink sm:text-[60px] lg:text-[72px]">
            Run your hiring from <span className="bv-hero-highlight">Claude, ChatGPT</span> or any AI agent.
          </motion.h1>
          <motion.p {...enter(0.12)} className="mx-auto mt-7 max-w-2xl text-[17px] leading-[1.7] text-bv-ink2 sm:text-lg">
            Connect Beatview once. Then open roles, check who applied, compare the shortlist and send interview invitations just by asking. Beatview has already screened and interviewed the candidates — your assistant brings back the evidence, and you make the call.
          </motion.p>
          <motion.div {...enter(0.2)} className="mt-9"><HeroActions centered onDemo={onDemo} secondary={{ label: 'Setup guide', href: MCP_DOCS_URL }} /></motion.div>
          <motion.div {...enter(0.26)} className="mx-auto mt-7 flex max-w-md flex-col items-center gap-3">
            <CopyField value={MCP_SERVER_URL} label="Server" className="w-full" />
            <p className="text-[13px] font-medium text-bv-mute">Works with {MCP_CLIENTS.join(', ')} and other MCP-compatible tools · included on every plan</p>
          </motion.div>
        </div>

        <motion.div initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25, ease: EASE }} className="mx-auto mt-16 max-w-[1120px] lg:mt-20">
          <AssistantDemo />
        </motion.div>
      </div>
    </section>
  );
}

export default function McpServer() {
  const [demo, setDemo] = useState(false);
  useSEO({ title: MCP_META.title, description: MCP_META.description, canonicalUrl: MCP_URL });

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Beatview MCP server',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Recruiting software',
    operatingSystem: 'Web',
    url: MCP_URL,
    description: MCP_DEFINITION,
    isPartOf: { '@type': 'SoftwareApplication', name: 'Beatview', url: SITE_URL },
    publisher: PUBLISHER,
    featureList: MCP_TOOLS.map(tool => `${tool.name}: ${tool.does}`),
    softwareHelp: { '@type': 'CreativeWork', url: MCP_DOCS_URL },
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'Free plan with one active job', url: `${SITE_URL}/pricing` },
  };

  return (
    <PageShell>
      <PageStructuredData name="Beatview MCP server for recruiting" description={MCP_META.description} url={MCP_URL} breadcrumbs={[{ name: 'Integrations', url: `${SITE_URL}/integrations` }, { name: 'MCP server' }]} />
      <StructuredData data={[productSchema, faqSchema(MCP_FAQS)]} />
      <Hero onDemo={() => setDemo(true)} />

      <Section tone="white" className="py-14 lg:py-16" narrow>
        <InShort label="What it is">{MCP_DEFINITION}</InShort>
      </Section>

      <Section tone="dark" labelledBy="done-title">
        <SectionHeading tone="dark" id="done-title" eyebrow="Why it is different" title={<>Your assistant doesn’t start from <span className="bv-hero-highlight">a pile of CVs.</span></>} description="Connecting an assistant to a database gives it records. Connecting it to Beatview gives it candidates your agent has already screened, interviewed and ranked — so every answer comes with evidence." />
        <IconCardGrid className="mt-14" columns={4} tone="dark" items={alreadyDone} />
      </Section>

      <Section tone="base" labelledBy="week-title">
        <SectionHeading id="week-title" eyebrow="A week on one role" title="Ask when you want. The agent keeps working when you don’t." description="Screening, stage rules and interview invitations run inside Beatview around the clock. The chat is where you check in and decide." />
        <div className="mt-16"><StepFlow steps={week} /></div>
      </Section>

      <Section tone="white" labelledBy="tools-title">
        <SectionHeading id="tools-title" eyebrow="What your assistant can do" title="Jobs, candidates and the pipeline — all from the chat." description="These are the actions the server exposes today. Moving a candidate, sending an invitation and switching on an AI interview all need your approval first." />
        <Reveal delay={0.1} className="mt-10">
          <DataTable
            caption="Actions available through the Beatview MCP server"
            columns={['Action', 'What it does', 'Ask it like this']}
            minWidth={760}
            rows={MCP_TOOLS.map(tool => [
              <span key={tool.name} className="flex flex-col gap-1"><code className="font-mono text-[13px] font-semibold text-bv-ink">{tool.name}</code>{tool.confirms && <span className="w-fit rounded-full bg-bv-orange-soft px-2 py-0.5 text-[11px] font-semibold text-bv-coral">Asks you first</span>}</span>,
              tool.does,
              <span key={`${tool.name}-ask`} className="text-bv-ink">{tool.ask}</span>,
            ])}
          />
        </Reveal>
      </Section>

      <Section tone="wash" labelledBy="prompts-title">
        <SectionHeading id="prompts-title" eyebrow="Prompt library" title="Copy a prompt. Paste it into your assistant." description="Beatview also offers starter prompts inside connected assistants — “Show me the pipeline”, “Compare the top candidates”, “Who needs action today” — so nobody has to learn a new way of asking." />
        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          {MCP_PROMPTS.map((group, gi) => (
            <Reveal key={group.title} delay={(gi % 2) * 0.06}>
              <h3 className="font-display text-xl font-semibold text-bv-ink">{group.title}</h3>
              <div className="mt-4 space-y-3">{group.prompts.map(prompt => <PromptCard key={prompt} prompt={prompt} />)}</div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="white" labelledBy="present-title">
        <SectionHeading id="present-title" eyebrow="Answers you can read" title="Beatview tells the assistant how a recruiter wants to see it." description="Instead of handing over raw data, the server sends layout guidance with each result, so Claude, ChatGPT and others show it the same way every time." />
        <IconCardGrid className="mt-12" columns={4} items={presentation} />
      </Section>

      <Section tone="dark" labelledBy="connect-title">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
          <SectionHeading tone="dark" id="connect-title" eyebrow="Connect in a minute" title="One address. One approval screen." description="No API keys to paste and nothing to install. Your assistant sends you to Beatview, you approve the connection, and you can see or revoke it any time." />
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-7">
              <p className="text-[13px] font-semibold uppercase tracking-wider text-white/50">Server address</p>
              <CopyField value={MCP_SERVER_URL} tone="dark" className="mt-3" />
              <p className="mt-7 text-[13px] font-semibold uppercase tracking-wider text-white/50">Configuration file</p>
              <pre className="mt-3 overflow-x-auto rounded-2xl bg-black/30 p-4 font-mono text-[13px] leading-6 text-white/85">{configJson}</pre>
              <a href={MCP_DOCS_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-bv-blue-bright hover:underline">Read the full setup guide <ArrowRight className="h-3.5 w-3.5" /></a>
            </div>
          </Reveal>
        </div>
        <div className="mt-16"><StepFlow tone="dark" steps={MCP_SETUP_STEPS} /></div>
      </Section>

      <Section tone="base" labelledBy="safety-title">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <SectionHeading id="safety-title" eyebrow="Control" title="Connected, not handed over." description="Candidate data is sensitive, and an agent that can act needs limits. These are built into the server." />
            <Reveal delay={0.1}>
              <div className="mt-8 flex items-start gap-3 rounded-2xl border border-bv-blue-line bg-white p-5">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-bv-success" />
                <p className="text-[15px] leading-6 text-bv-ink2">AI scores are always presented as signals to review, never as a hiring decision. A person decides who moves forward.</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.05}><CheckList items={MCP_SAFETY} /></Reveal>
        </div>
      </Section>

      <Section tone="white" labelledBy="who-title">
        <SectionHeading id="who-title" eyebrow="Who uses it" title="Built for agency desks. Open to any agent." />
        <IconCardGrid className="mt-12" items={audiences} />
      </Section>

      <FaqSection title="Questions about the MCP server" items={MCP_FAQS} idPrefix="mcp-faq" tone="wash" />

      <RelatedLinks eyebrow="Set-up guides" title="Connect your assistant" items={[
        { title: 'Beatview + Claude', body: 'Add Beatview as a custom connector in Claude.', href: '/integrations/claude', icon: Bot },
        { title: 'Beatview + ChatGPT', body: 'Publish Beatview as a custom app in your ChatGPT workspace.', href: '/integrations/chatgpt', icon: Bot },
        { title: 'Beatview + Gemini', body: 'Add Beatview as a custom Connected App in Gemini.', href: '/integrations/gemini', icon: Bot },
        { title: 'Recruiting MCP servers compared', body: 'Which recruiting tools work with AI assistants, and what each one exposes.', href: '/guides/recruiting-mcp-servers', icon: Scale },
      ]} />

      <RelatedLinks title="What your assistant is connected to" items={PRODUCT_LINKS} tone="base" />

      <CtaPanel eyebrow="Try it on a live role" title={<>Open your next role <span className="bv-hero-highlight">from the chat.</span></>} description="Create a free Beatview account, connect your assistant and ask it to open a job. The free plan includes one active role." primaryHref={APP_URL} onDemo={() => setDemo(true)} bullets={['Included on every plan', 'Asks before it acts', 'Recruiters keep every decision']} />
      <DemoRequestDialog open={demo} onOpenChange={setDemo} />
    </PageShell>
  );
}
